"use client";

import { Zap, Printer, Gauge, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  icon: "zap" | "printer" | "gauge" | "shield";
  value: string;
  numeric: number;
  decimals: number;
  unit: string;
  label: string;
  isTime: boolean;
};
const stats: Stat[] = [{
  icon: "zap",
  value: "15",
  numeric: 15,
  decimals: 0,
  unit: "sec",
  label: "heat press",
  isTime: false
}, {
  icon: "printer",
  value: "1440",
  numeric: 1440,
  decimals: 0,
  unit: "DPI",
  label: "Print Resolution",
  isTime: false
}, {
  icon: "gauge",
  value: "98",
  numeric: 98,
  decimals: 0,
  unit: "%",
  label: "Color Accuracy",
  isTime: false
}, {
  icon: "shield",
  value: "50",
  numeric: 50,
  decimals: 0,
  unit: "+",
  label: "Wash Cycles",
  isTime: true
}];
const tickerItems = ["CMYK+W TECHNOLOGY", "DURABLE WASH", "FAST DRY INKS", "PRECISION HEAT PRESS", "CMYK+W TECHNOLOGY", "DURABLE WASH", "FAST DRY INKS", "PRECISION HEAT PRESS"];
function StatIcon({
  type
}: {
  type: Stat["icon"];
}) {
  const className = "size-10 stroke-[1.5]";
  switch (type) {
    case "zap":
      return <Zap className={className} />;
    case "printer":
      return <Printer className={className} />;
    case "gauge":
      return <Gauge className={className} />;
    case "shield":
      return <ShieldCheck className={className} />;
  }
}
function AnimatedNumber({
  target,
  decimals,
  isTime,
  display,
  start
}: {
  target: number;
  decimals: number;
  isTime: boolean;
  display: string;
  start: boolean;
}) {
  const [value, setValue] = useState(isTime ? display : "0");
  useEffect(() => {
    if (!start) return;
    if (isTime) {
      setValue(display);
      return;
    }
    const duration = 1500;
    const startTime = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;
      setValue(current.toFixed(decimals));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, decimals, isTime, display]);
  return <span>{value}</span>;
}
export default function TechnicalSpecifications() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="720" id="technical-specifications" className="dark relative overflow-hidden bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div ref={headerRef} className={`flex flex-col gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-primary" style={{
            letterSpacing: "0.25em"
          }}>
              § 01 — Technical Specs
            </span>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="inline-flex items-center gap-2 border border-border px-3 py-1 rounded-md">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-lg size-2 bg-primary"></span>
              </span>
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-secondary-foreground/80">
                Operational
              </span>
            </div>
          </div>

          <h2 className="font-default font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-foreground">
            Precision Printing
          </h2>
        </div>

        <div ref={gridRef} className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {stats.map((stat, i) => <div data-index={i} key={stat.label} className={`group relative rounded-lg border border-border bg-card backdrop-blur-sm p-7 md:p-8 flex flex-col gap-6 transition-all duration-700 ease-out hover:border-primary ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
        }}>
              <div className="text-primary">
                <StatIcon type={stat.icon} />
              </div>

              <div className="flex flex-col">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-default font-bold text-foreground leading-none text-6xl md:text-7xl lg:text-[96px] tracking-tighter">
                    <AnimatedNumber target={stat.numeric} decimals={stat.decimals} isTime={stat.isTime} display={stat.value} start={gridVisible} />
                  </span>
                  <span className="font-default font-semibold text-secondary text-2xl md:text-3xl leading-none">
                    {stat.unit}
                  </span>
                </div>

                <div className="mt-5 pt-5 border-t border-border">
                  <span className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-secondary" style={{
                letterSpacing: "0.2em"
              }}>
                    {stat.label}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div className="mt-20 md:mt-28 overflow-hidden">
        <div className="flex whitespace-nowrap animate-[ticker_40s_linear_infinite]">
          {tickerItems.map((item, i) => <div data-index={i} key={i} className="flex items-center shrink-0">
              <span className="font-mono text-secondary/20 text-6xl md:text-7xl lg:text-8xl px-8 tracking-tighter">
                {item}
              </span>
              <span className="font-mono text-primary/40 text-6xl md:text-7xl lg:text-8xl px-4">
                ·
              </span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}